import React, { useMemo } from 'react';
import type { CartesianPlanePayload } from '../../core/types/payloads/cartesian_plane.types';

const GRID_STROKE = '#E2E8F0';
const AXIS_STROKE = '#94A3B8';
const TICK_FILL = '#64748B';
const DEFAULT_POLYGON_COLOR = '#8A7BFA';
const POINT_FILL = '#2563EB';

const CANVAS = 420;
const PADDING = 36;

function buildMapper(payload: CartesianPlanePayload) {
  const plot = CANVAS - PADDING * 2;
  const gridSize = payload.gridSize;

  if (payload.quadrants === '1') {
    const xMin = 0;
    const xMax = gridSize;
    const yMin = 0;
    const yMax = gridSize;

    return {
      xMin,
      xMax,
      yMin,
      yMax,
      toPixel: (x: number, y: number) => ({
        px: PADDING + ((x - xMin) / (xMax - xMin || 1)) * plot,
        py: CANVAS - PADDING - ((y - yMin) / (yMax - yMin || 1)) * plot,
      }),
      xTicks: Array.from({ length: gridSize + 1 }, (_, index) => index),
      yTicks: Array.from({ length: gridSize + 1 }, (_, index) => index),
      origin: {
        px: PADDING,
        py: CANVAS - PADDING,
      },
    };
  }

  const xMin = -gridSize;
  const xMax = gridSize;
  const yMin = -gridSize;
  const yMax = gridSize;
  const span = gridSize * 2 || 1;

  return {
    xMin,
    xMax,
    yMin,
    yMax,
    toPixel: (x: number, y: number) => ({
      px: PADDING + ((x - xMin) / span) * plot,
      py: CANVAS - PADDING - ((y - yMin) / span) * plot,
    }),
    xTicks: Array.from({ length: span + 1 }, (_, index) => xMin + index),
    yTicks: Array.from({ length: span + 1 }, (_, index) => yMin + index),
    origin: {
      px: PADDING + plot / 2,
      py: CANVAS - PADDING - plot / 2,
    },
  };
}

function polygonFill(color?: string) {
  const base = color?.trim() || DEFAULT_POLYGON_COLOR;
  if (base.startsWith('rgba(') || base.startsWith('rgb(')) return base;
  if (base.startsWith('#') && base.length === 7) {
    const r = parseInt(base.slice(1, 3), 16);
    const g = parseInt(base.slice(3, 5), 16);
    const b = parseInt(base.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, 0.2)`;
  }
  return 'rgba(138, 123, 250, 0.2)';
}

function polygonStroke(color?: string) {
  const base = color?.trim() || DEFAULT_POLYGON_COLOR;
  if (base.startsWith('#')) return base;
  return DEFAULT_POLYGON_COLOR;
}

export function CartesianPlaneView({ payload }: { payload: CartesianPlanePayload }) {
  const mapper = useMemo(() => buildMapper(payload), [payload]);

  const gridLines: React.ReactNode[] = [];
  for (const x of mapper.xTicks) {
    const { px } = mapper.toPixel(x, 0);
    gridLines.push(
      <line key={`gx-${x}`} x1={px} y1={PADDING} x2={px} y2={CANVAS - PADDING} stroke={GRID_STROKE} strokeWidth={1} />,
    );
  }
  for (const y of mapper.yTicks) {
    const { py } = mapper.toPixel(0, y);
    gridLines.push(
      <line key={`gy-${y}`} x1={PADDING} y1={py} x2={CANVAS - PADDING} y2={py} stroke={GRID_STROKE} strokeWidth={1} />,
    );
  }

  const axisXStart = mapper.toPixel(mapper.xMin, 0);
  const axisXEnd = mapper.toPixel(mapper.xMax, 0);
  const axisYStart = mapper.toPixel(0, mapper.yMin);
  const axisYEnd = mapper.toPixel(0, mapper.yMax);

  return (
    <div className="space-y-4">
      <p className="text-sm leading-relaxed text-slate-700 print:text-[#334155]">{payload.instruction}</p>
      <svg viewBox={`0 0 ${CANVAS} ${CANVAS}`} className="h-auto w-full max-w-md" role="img" aria-label={payload.instruction}>
        <rect x={PADDING} y={PADDING} width={CANVAS - PADDING * 2} height={CANVAS - PADDING * 2} fill="#FFFFFF" />
        {gridLines}
        <line
          x1={axisXStart.px}
          y1={mapper.origin.py}
          x2={axisXEnd.px}
          y2={mapper.origin.py}
          stroke={AXIS_STROKE}
          strokeWidth={1.75}
        />
        <line
          x1={mapper.origin.px}
          y1={axisYStart.py}
          x2={mapper.origin.px}
          y2={axisYEnd.py}
          stroke={AXIS_STROKE}
          strokeWidth={1.75}
        />
        {mapper.xTicks.map((tick) => {
          const { px, py } = mapper.toPixel(tick, 0);
          if (tick === 0 && payload.quadrants === '4') return null;
          return (
            <text key={`xt-${tick}`} x={px} y={py + 14} textAnchor="middle" fontSize="10" fill={TICK_FILL}>
              {tick}
            </text>
          );
        })}
        {mapper.yTicks.map((tick) => {
          const { px, py } = mapper.toPixel(0, tick);
          if (tick === 0 && payload.quadrants === '4') return null;
          return (
            <text key={`yt-${tick}`} x={px - 10} y={py + 3} textAnchor="end" fontSize="10" fill={TICK_FILL}>
              {tick}
            </text>
          );
        })}
        <text x={CANVAS - PADDING + 4} y={mapper.origin.py + 4} fontSize="11" fill={TICK_FILL}>
          x
        </text>
        <text x={mapper.origin.px + 6} y={PADDING - 8} fontSize="11" fill={TICK_FILL}>
          y
        </text>
        {(payload.polygons ?? []).map((polygon, index) => {
          const points = polygon.vertices
            .map((vertex) => {
              const { px, py } = mapper.toPixel(vertex.x, vertex.y);
              return `${px},${py}`;
            })
            .join(' ');
          return (
            <polygon
              key={`poly-${index}`}
              points={points}
              fill={polygonFill(polygon.color)}
              stroke={polygonStroke(polygon.color)}
              strokeWidth={1.5}
            />
          );
        })}
        {(payload.points ?? []).map((point, index) => {
          const { px, py } = mapper.toPixel(point.x, point.y);
          return (
            <g key={`pt-${index}`}>
              <circle cx={px} cy={py} r={4.5} fill={POINT_FILL} stroke="#FFFFFF" strokeWidth={1.25} />
              {point.label ? (
                <text x={px + 8} y={py - 8} fontSize="11" fontWeight="600" fill={TICK_FILL}>
                  {point.label}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
